import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, inject, OnDestroy, OnInit, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { InputText } from 'primeng/inputtext';
import { Tooltip } from 'primeng/tooltip';
import { map, of, Subscription } from 'rxjs';
import { ClinicDetail } from 'src/app/dashboard/shared/clinic.models';
import { AdminService } from 'src/app/float-pool/admin/admin.service';
import { VirtualLobbyService } from 'src/app/virtual-lobby/services/virtual-lobby.service';

import { environment } from '../../../../environments/environment';
import { PatientScheduleService } from '../../../schedule-wise/schedule-patient/services/patient-schedule.service';
import { ProviderService } from '../../../schedule-wise/services/provider.service';
import { QuadrupleClickDirective } from '../../directives/quadrupleClick';
import { ClinicService } from '../../services/clinic.service';
import { FloatService } from '../../services/float.service';
import { SessionService, UserLogin } from '../../services/session.service';
import { SecretMenuComponent } from '../secret-menu/secret-menu.component';
import { TopHeaderComponent } from '../top-header/top-header.component';

@Component({
  selector: 'upper-menu-component',
  templateUrl: './upper-menu.html',
  styleUrls: ['./upper-menu-component.scss'],
  imports: [
    TopHeaderComponent,
    RouterLink,
    RouterLinkActive,
    NgClass,
    FormsModule,
    InputText,
    QuadrupleClickDirective,
    AsyncPipe,
    Tooltip,
  ],
  providers: [DialogService],
})
export class UpperMenuComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private providerService = inject(ProviderService);
  private ref = inject(ChangeDetectorRef);
  private messageService = inject(MessageService);
  private adminService = inject(AdminService);
  private cookieService = inject(CookieService);
  private sessionService = inject(SessionService);
  private floatSerivce = inject(FloatService);
  private clinicService = inject(ClinicService);
  private virtualLobbyService = inject(VirtualLobbyService);
  private patientScheduleService = inject(PatientScheduleService);
  dialogService = inject(DialogService);

  userLogin = '--';
  navLinks: INavLink[];
  selectedClinic: ClinicDetail;
  searchClinicResult: any[];
  allClinics: ClinicDetail[];
  filterClinicList: any[];
  centerCodeList: any[];
  searchClinicInput = '';
  displaySearch = false;
  preselectClinicId: number | null;

  private readonly searchClinicInputRef = viewChild<ElementRef<HTMLInputElement>>('searchClinicInputRef');

  private subscriptions: Subscription[] = [];
  public noUser = true;
  public showClinicList = false;
  public lockout = of(false);

  ngOnInit() {
    this.subscriptions.push(
      this.clinicService.getClinic().subscribe((clinic) => {
        if (clinic) {
          this.selectedClinic = clinic;
          // Trigger change detection to update the view if clinic is selected from hierarchy page
          this.ref.detectChanges();
        }
      }),
    );

    this.subscriptions.push(
      this.sessionService.currentUser$.subscribe({
        next: (currentUser) => {
          this.setup(currentUser);
        },
      }),
    );

    // Update the nav badge when missed treatment count changes from the patient schedule page
    this.subscriptions.push(
      this.patientScheduleService
        .watchMissedTreatmentsCount()
        .subscribe((count) => this.setMissedTreatmentsCount(count)),
    );

    this.checkLockout();
  }

  ngOnDestroy() {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }

    this.dialogService.dialogComponentRefMap.forEach((dialog) => {
      dialog.destroy();
    });
  }

  async setup(userInfo: UserLogin) {
    this.showClinicList = false;
    // handle the user no longer existing
    if (!userInfo) {
      this.noUser = true;
      this.ref.detectChanges();
      return;
    } else {
      this.noUser = false;
    }
    this.userLogin = userInfo.userLogin ? this.sessionService.getUserLogin().userLogin : '--';
    this.preselectClinicId = this.clinicService.getPreselectedClinic();
    const userId = userInfo.userId;
    this.showClinicList = true;
    this.providerService.getClinicList(userId.toString()).subscribe((clinics) => {
      this.allClinics = clinics;
      this.selectedClinic = this.preselectClinicId
        ? this.allClinics.filter((clinic) => clinic.IdCenter === this.preselectClinicId)[0] || this.allClinics[0]
        : this.allClinics[0];
      this.clinicService.setClinic(this.selectedClinic);
      this.searchClinicResult = this.getFirstClinics(clinics);
      this.filterClinicList = clinics;
      this.getVirtualLobbyNumber(this.selectedClinic['IdCenter']);
      this.getMissedTreatmentsCount(this.selectedClinic['IdCenter']);
    });
    this.setUpperMenuLinks();
  }

  async logout() {
    this.router.navigate(['/logout']);
  }

  goToLink(nav: INavLink) {
    if (nav.subMenu) {
      return;
    }
    nav.url ? this.router.navigate([nav.url]) : (window.location.href = nav.externalUrl);
  }

  showSearch() {
    this.displaySearch = !this.displaySearch;

    if (this.displaySearch) {
      this.ref.detectChanges();
      this.searchClinicInputRef()?.nativeElement.focus();
    }
  }

  searchClinicInputChange() {
    this.filterClinicList = this.allClinics.filter((clinic) => {
      return clinic.CenterDisplayName.toLowerCase().includes(this.searchClinicInput.toLowerCase());
    });
    this.centerCodeList = this.allClinics.filter((c) => {
      return c.CenterCode.toLowerCase() === this.searchClinicInput.toLowerCase();
    });
    this.searchClinicResult = this.getFirstClinics(this.filterClinicList);
    this.ref.detectChanges();
  }

  selectClinicLink(clinic) {
    this.selectedClinic = clinic;
    this.clinicService.setClinic(this.selectedClinic);
    this.displaySearch = false;
    this.searchClinicInput = '';
    this.filterClinicList = this.allClinics;
    this.centerCodeList = [];
    this.searchClinicResult = this.getFirstClinics(this.filterClinicList);
    this.setCookiesAndSessionStorage();
    this.getVirtualLobbyNumber(this.selectedClinic['IdCenter']);
    this.getMissedTreatmentsCount(this.selectedClinic['IdCenter']);
  }

  goToClinic() {
    if (!this.centerCodeList.length) {
      return this.messageService.add({
        severity: 'error',
        detail: `${this.searchClinicInput} is not a valid Clinic ID`,
      });
    }
    this.selectClinicLink(this.centerCodeList[0]);
  }
  openSecretMenu() {
    if (!environment.production) {
      this.dialogService.open(SecretMenuComponent, { header: 'Secret Menu', closable: true });
    }
  }

  private getFirstClinics(clinicList): any[] {
    return clinicList.length > 25 ? clinicList.slice(0, 25) : clinicList;
  }

  private setCookiesAndSessionStorage() {
    sessionStorage.setItem('fsw.angular._FMCSW_CENTERID', this.selectedClinic.IdCenter.toString());
    this.cookieService.set('LCENTER', this.selectedClinic.IdCenter.toString(), null, '/');
    const swCenterCookie = JSON.stringify({
      CENTERCODE: this.selectedClinic.CenterCode,
      CENTERID: this.selectedClinic.IdCenter,
      CENTERNAME: this.selectedClinic.CenterName,
    });
    this.cookieService.delete('SW_CENTER', '/', '.fmcschedule.com');
    this.cookieService.set('SW_CENTER', swCenterCookie, null, '/', '.fmcschedule.com');
  }

  private async setUpperMenuLinks() {
    this.navLinks = [
      {
        displayText: 'Staff',
        url: '/schedule/staff',
        hasAccess: this.sessionService.checkUserPermissionsAny(['SS']),
      },
      {
        displayText: 'Patient',
        url: '/schedule/patient',
        hasAccess: this.sessionService.checkUserPermissionsAny(['VS']),
        counter: 0,
        counterTooltip:
          '# of scheduled patients who have not been treated in 30 days is indicated by a red "!" next to their name.',
      },
      {
        displayText: 'WISE',
        url: '/dashboard',
        hasAccess: this.sessionService.checkUserPermissionsAny(['LP']),
      },
      {
        displayText: 'SWAC',
        url: `/swac/clinic`,
        hasAccess: this.sessionService.checkUserPermissionsAny(['SWAC', 'SWACPERM']),
        subMenu: [
          {
            displayText: 'Clinic Search',
            url: `/swac/clinic`,
            hasAccess: this.sessionService.checkUserPermissionsAny(['SWAC', 'SWACPERM']),
          },
          {
            displayText: 'SWAC Dashboard',
            url: `/swac/patients`,
            hasAccess:
              this.sessionService.checkUserPermissionsAny(['SWAC', 'SWACPERM']) &&
              !this.sessionService.checkUserPermissionsAny(['SWACRO']),
          },
        ],
      },
      {
        displayText: 'Virtual Lobby',
        url: `/virtual-lobby`,
        hasAccess: this.sessionService.checkUserPermissionsAny(['LOBBY', 'ROLOBBY']),
        counter: 0,
      },
      {
        displayText: 'Reports',
        url: `/admin/reports`,
        hasAccess: this.sessionService.checkUserPermissionsAny(['REPORTSNEW', 'REPORTS', 'OPTIMIZER', 'SYSADMIN']),
      },
      {
        displayText: 'Admin',
        hasAccess: this.sessionService.checkUserPermissionsAny(['ADMIN']),
        subMenu: [
          {
            displayText: 'Users',
            hasAccess: this.sessionService.checkUserPermissionsAny(['USERS']),
            url: `/admin/user`,
          },
          {
            displayText: 'Hierarchy',
            hasAccess: this.sessionService.checkUserPermissionsAny(['SYSADMIN']),
            url: `/admin/hierarchy`,
          },
          {
            displayText: 'Configuration',
            hasAccess: this.sessionService.checkUserPermissionsAny(['CONFIG']),
            url: `/podconfig`,
          },
          {
            displayText: 'User Roles',
            hasAccess: this.sessionService.checkUserPermissionsAny(['USERROLES']),
            url: `/admin/user-roles`,
          },
          {
            displayText: 'Announcerator',
            hasAccess: this.sessionService.checkUserPermissionsAny(['MAINT']),
            url: `/admin/annoucerator`,
          },
          {
            displayText: 'Maintenance Mode',
            hasAccess: this.sessionService.checkUserPermissionsAny(['MAINT']),
            url: `/admin/maintenance`,
          },
          {
            displayText: 'Tooltips',
            hasAccess: this.sessionService.checkUserPermissionsAny(['SYSADMIN']),
            url: '/dashboard/admin',
          },
        ],
      },
      {
        displayText: 'My Schedule',
        url: '/staff-schedule/my-schedule',
        hasAccess: this.sessionService.checkUserPermissionsAny(['_MYSCHEDULE', 'SYSADMIN']),
        subMenu:
          // only show the drop down if you are eligible or permanent
          this.sessionService.checkUserPermissionsAny(['FLOATELIGIBLE'])
            ? [
                {
                  displayText: 'Float Settings',
                  url: '/float-pool/register/summary',
                  hasAccess: this.sessionService.checkUserPermissionsAny(['FLOATELIGIBLE']),
                },
                {
                  displayText: 'Available Shifts',
                  url: '/float-pool/provider/available-shifts',
                  hasAccess: this.sessionService.checkUserPermissionsAny(['FLOATELIGIBLE']),
                },
              ]
            : undefined,
      },
    ];

    this.ref.detectChanges();

    this.setRegistrationStatus();
  }
  private setRegistrationStatus() {
    if (!this.sessionService.checkUserPermissionsAny(['FLOATELIGIBLE'])) return;

    this.floatSerivce.getRegistrationStatus().subscribe((registration) => {
      // you are already completed registration so carry on
      if (
        registration.status.completedNotification &&
        registration.status.completedClinics &&
        registration.status.completedSummary
      )
        return;

      let registrationUrl = '/float-pool/register/notifications';
      if (registration.status?.completedClinics) {
        registrationUrl = '/float-pool/register/summary';
      } else if (registration.status?.completedNotification) {
        registrationUrl = '/float-pool/register/clinics';
      }

      const myScheduleLink = this.navLinks.find((link) => link.displayText === 'My Schedule');
      // set the counter to 1 so that people know there is something there to do
      myScheduleLink.counter = 1;
      myScheduleLink.subMenu = [
        {
          displayText: 'Register Float',
          url: registrationUrl,
          hasAccess: this.sessionService.checkUserPermissionsAny(['FLOATELIGIBLE']),
        },
      ];

      this.ref.detectChanges();
    });
  }
  private async setVirtualLobbyCount(virtualLobbyCount) {
    const virtualLobbyLink = this.navLinks.find((link) => link.displayText === 'Virtual Lobby');
    virtualLobbyLink.counter = virtualLobbyCount;
    this.ref.detectChanges();
  }
  private getVirtualLobbyNumber(selectedClinicId) {
    this.providerService.getVirtualLobbyCount(selectedClinicId).subscribe((count) => {
      this.setVirtualLobbyCount(count);
    });
  }

  private getMissedTreatmentsCount(idCenter: number) {
    this.patientScheduleService.getMissedTreatments(idCenter).subscribe({
      next: (res) => this.setMissedTreatmentsCount(res.patientIds.length),
      error: () => this.setMissedTreatmentsCount(0),
    });
  }

  private setMissedTreatmentsCount(count: number) {
    const patientLink = this.navLinks?.find((link) => link.displayText === 'Patient');
    if (patientLink) {
      patientLink.counter = count;
      this.ref.detectChanges();
    }
  }
  private checkLockout() {
    this.lockout = this.virtualLobbyService.getPatientRequiresPlacement().pipe(
      map(
        (patientRequiresPlacement) =>
          patientRequiresPlacement &&
          // Only people with the jail permission go to jail.
          this.sessionService.checkUserPermissionsAny(['VLJAIL']),
      ),
    );
  }
}

export interface INavLink {
  displayText: string;
  url?: string;
  hasAccess: boolean;
  externalUrl?: string;
  subMenu?: INavLink[];
  counter?: number;
  counterTooltip?: string;
}
